<template>
  <div class="verifying-status-panel" role="status" aria-live="polite" aria-busy="true">
    <div class="verifying-status-panel__hero verifying-status-panel__hero--motor" aria-hidden="true">
      <slot name="loader">
        <Loading />
      </slot>
    </div>

    <h3 class="verifying-status-panel__title">{{ title }}</h3>
    <p v-if="subtitle" class="verifying-status-panel__subtitle">{{ subtitle }}</p>

    <div class="verifying-status-panel__progress-wrap">
      <div
        class="verifying-status-panel__progress-track"
        role="progressbar"
        :aria-valuenow="safePercent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="progressLabel || title"
      >
        <div
          class="verifying-status-panel__progress-fill"
          :style="{ width: `${safePercent}%` }"
        />
      </div>
      <p v-if="progressLabel" class="verifying-status-panel__progress-label">
        {{ progressLabel }}
      </p>
    </div>

    <ol v-if="safeSteps.length" class="verifying-status-panel__steps">
      <li
        v-for="(label, index) in safeSteps"
        :key="`${index}-${label}`"
        class="verifying-status-panel__step"
        :class="`verifying-status-panel__step--${stepState(index)}`"
      >
        <span
          class="verifying-status-panel__step-icon"
          :class="`verifying-status-panel__step-icon--${stepState(index)}`"
          aria-hidden="true"
        >
          <svg
            v-if="stepState(index) === 'completed'"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        <p class="verifying-status-panel__step-label">{{ label }}</p>
      </li>
    </ol>
  </div>
</template>

<script>
import Loading from '@/components/uiResuable/Loading.vue';

export default {
  name: 'VerifyingStatusPanel',
  components: { Loading },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    progressPercent: { type: [Number, String], default: 0 },
    progressLabel: { type: String, default: '' },
    steps: { type: Array, default: () => [] },
    activeStepIndex: { type: [Number, String], default: 0 },
  },
  computed: {
    safePercent() {
      const value = Number(this.progressPercent) || 0;
      return Math.min(100, Math.max(0, Math.round(value)));
    },
    safeSteps() {
      return Array.isArray(this.steps) ? this.steps.filter((step) => typeof step === 'string') : [];
    },
    safeActiveIndex() {
      const index = Number(this.activeStepIndex) || 0;
      return Math.min(Math.max(0, index), Math.max(0, this.safeSteps.length - 1));
    },
  },
  methods: {
    stepState(index) {
      if (index < this.safeActiveIndex) return 'completed';
      if (index === this.safeActiveIndex) return 'active';
      return 'pending';
    },
  },
};
</script>

<style scoped>
.verifying-status-panel {
  display: flex;
  width: 100%;
  max-width: min(100%, 36rem);
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding-inline: 0.5rem;
  text-align: center;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .verifying-status-panel {
    max-width: min(100%, 42rem);
    padding-inline: 1rem;
  }
}

.verifying-status-panel__hero {
  position: relative;
  width: 5rem;
  height: 5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.verifying-status-panel__hero--motor :deep(.spinner) {
  position: relative;
  width: 2.5rem;
  height: 2.5rem;
}

.verifying-status-panel__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  color: #1e3a5f;
}

.verifying-status-panel__subtitle {
  margin: 0;
  max-width: min(100%, 32rem);
  font-size: 0.9375rem;
  line-height: 1.45;
  color: #6b7c93;
}

.verifying-status-panel__progress-wrap {
  width: 100%;
  max-width: min(100%, 32rem);
}

.verifying-status-panel__progress-track {
  height: 0.375rem;
  overflow: hidden;
  border-radius: 9999px;
  background: #e8eef5;
}

.verifying-status-panel__progress-fill {
  height: 100%;
  border-radius: 9999px;
  background: #1e3a5f;
  transition: width 0.2s linear;
}

.verifying-status-panel__progress-label {
  margin: 0.625rem 0 0;
  font-size: 0.875rem;
  color: #8a97a8;
}

.verifying-status-panel__steps {
  display: flex;
  width: 100%;
  max-width: min(100%, 28rem);
  flex-direction: column;
  gap: 0.875rem;
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
  text-align: left;
}

@media (min-width: 768px) {
  .verifying-status-panel__steps {
    max-width: min(100%, 34rem);
  }
}

.verifying-status-panel__step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.verifying-status-panel__step-icon {
  display: flex;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
}

.verifying-status-panel__step-icon--completed {
  background: #22c55e;
  color: #fff;
}

.verifying-status-panel__step-icon--completed svg {
  width: 0.75rem;
  height: 0.75rem;
}

.verifying-status-panel__step-icon--active {
  border: 2px solid rgba(30, 58, 95, 0.35);
  background: transparent;
}

.verifying-status-panel__step-icon--active::after {
  content: '';
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: #1e3a5f;
}

.verifying-status-panel__step-icon--pending {
  border: 2px solid #d1d9e4;
  background: transparent;
}

.verifying-status-panel__step-label {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.9375rem;
  line-height: 1.35;
  color: #9aa5b4;
  overflow-wrap: anywhere;
  word-break: break-word;
}

@media (min-width: 768px) {
  .verifying-status-panel__step-label {
    white-space: nowrap;
    overflow-wrap: normal;
    word-break: normal;
  }
}

.verifying-status-panel__step--active .verifying-status-panel__step-label {
  font-weight: 600;
  color: #1e3a5f;
}

.verifying-status-panel__step--completed .verifying-status-panel__step-label {
  font-weight: 500;
  color: #1e3a5f;
}
</style>
